import { memo, useRef, useState, useEffect, useLayoutEffect, useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import ContentEditable from 'react-contenteditable';
import logger from '../../utils/logger';
import {
  checkRecordWithGetLinkDraftData,
  checkRecordWithGetLinkPublishData,
  getdataByNodeId,
} from '../../api/api';
import { BreadcrumbsContext } from '../../context/BreadcrumbsContext';
import { buildBreadcrumbs } from '../../utils/buildBreadcrumbs';
import { getLevelKey } from '../../utils/getLevel';
import { useProcessNavigation } from '../../hooks/useProcessNavigation';
import { stripHtml } from '../../components/StripHtml';
import { useTranslation } from '../../hooks/useTranslation';
import CustomAlert from '../../components/CustomAlert';
import {
  hyphenateText,
  cleanHyphens,
  saveCaretPosition,
  restoreCaretPosition,
} from '../../utils/hyphenateText';

const SwimlaneArrowBoxNode = ({ data, mode, editable: propEditable, langCode }) => {
  const editable = propEditable !== undefined ? propEditable : mode === 'edit';
  const [title, setTitle] = useState(() =>
    hyphenateText(stripHtml(data?.details?.title || data?.label || ''), langCode)
  );
  const [autoFocus, setAutoFocus] = useState(data.autoFocus);
  const [isHovered, setIsHovered] = useState(false);
  const { addBreadcrumb, removeBreadcrumbsAfter } = useContext(BreadcrumbsContext);
  const contentEditableRef = useRef(null);
  const caretOffsetRef = useRef(null);
  const { goToProcess } = useProcessNavigation();
  const t = useTranslation();

  useLayoutEffect(() => {
    if (caretOffsetRef.current !== null && contentEditableRef.current) {
      if (document.activeElement === contentEditableRef.current) {
        restoreCaretPosition(contentEditableRef.current, caretOffsetRef.current);
      }
      caretOffsetRef.current = null;
    }
  });

  // Sync label (only when not focused)
  useEffect(() => {
    const isFocused =
      contentEditableRef.current && document.activeElement === contentEditableRef.current;
    if (!isFocused) {
      const incoming = data?.details?.title !== undefined ? data?.details?.title : data?.label;
      if (incoming !== undefined) {
        setTitle(hyphenateText(stripHtml(incoming), langCode));
      }
    }
  }, [data?.details?.title, data?.label, langCode]);

  // Handle auto focus in edit mode
  useEffect(() => {
    if (editable && autoFocus && contentEditableRef.current) {
      setTimeout(() => {
        const el = contentEditableRef.current;
        el.focus({ preventScroll: true });

        // Move caret to the end of the content
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        setAutoFocus(false);
      }, 0);
    }
  }, [autoFocus, editable]);

  const handleBoxClick = (e) => {
    if (editable) {
      // If the click is already on the ContentEditable, don't do anything
      if (e.target.classList.contains('nodrag')) {
        return;
      }
      if (contentEditableRef.current && document.activeElement !== contentEditableRef.current) {
        contentEditableRef.current.focus();

        const el = contentEditableRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      handleLinkClick();
    }
  };

  const handleLinkClick = async () => {
    if (data.processlink) {
      try {
        const response = await getdataByNodeId(data.processlink, 'draft');
        if (response.data && response.data.length > 0) {
          const user_id = response.data[0].user_id;
          const Process_id = response.data[0].process_id;
          const idVal = response.data[0].process_id;

          let newLevel = 1;
          if (data.processlink !== null) {
            const match = data.processlink.match(/^level(\d+)/);
            if (match && match[1]) {
              const currentLevel = parseInt(match[1], 10);
              newLevel = currentLevel + 1;
            }
          }
          const levelParam = getLevelKey(newLevel, data.processlink);

          const isPublishedMode = mode === 'published';
          const nodeData = isPublishedMode
            ? await checkRecordWithGetLinkPublishData(
                levelParam,
                parseInt(user_id),
                Process_id,
                data.processlink
              )
            : await checkRecordWithGetLinkDraftData(
                levelParam,
                parseInt(user_id),
                Process_id,
                data.processlink
              );

          if (nodeData.status === true) {
            removeBreadcrumbsAfter(1);
            const breadcrumbs = buildBreadcrumbs(
              nodeData.allNodes,
              nodeData.ids,
              Process_id,
              isPublishedMode ? 'Publish' : undefined
            );

            breadcrumbs.forEach(({ label, path, state }) => {
              addBreadcrumb(label, path, state);
            });

            const nextMode = isPublishedMode ? 'published' : 'draft';
            const view = nodeData.Page_Title === 'Swimlane' ? 'swimlane' : 'map';

            goToProcess({
              mode: nextMode,
              view,
              processId: idVal,
              level: newLevel,
              parentId: data.processlink,
            });
          } else {
            CustomAlert.warning(
              t('warning') || 'Warning',
              t('first_create_next_model_of_this_existing_model') ||
                'First create next model of this existing model'
            );
          }
        } else {
          logger.error('No data found in response.data');
        }
      } catch (error) {
        logger.error('Error fetching link data:', error);
      }
    }
  };

  const handleChange = (e) => {
    const el = contentEditableRef.current;
    const caretOffset = el ? saveCaretPosition(el) : null;
    const rawVal = e.target.value;
    const cleanVal = cleanHyphens(rawVal);
    const hyphenated = hyphenateText(rawVal, langCode);

    caretOffsetRef.current = caretOffset;
    setTitle(hyphenated);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }

    if (el && caretOffset !== null) {
      requestAnimationFrame(() => {
        if (caretOffsetRef.current !== null && document.activeElement === el) {
          restoreCaretPosition(el, caretOffset);
          caretOffsetRef.current = null;
        }
      });
    }
  };

  const handleBlur = () => {
    const rawVal = contentEditableRef.current
      ? contentEditableRef.current.innerText || contentEditableRef.current.textContent || title
      : title;
    const cleanVal = cleanHyphens(rawVal);
    const hyphenated = hyphenateText(cleanVal, langCode);
    setTitle(hyphenated);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleFocus = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      style={{
        ...styles.wrapper,
        filter:
          data?.processlink && data?.hasNextLevel
            ? 'drop-shadow(rgba(0, 0, 0, 0.31) 0px 0px 10px)'
            : 'none',
        cursor: editable ? 'move' : data.processlink ? 'pointer' : 'default',
      }}
      onClick={handleBoxClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.arrowBox}>
        {editable ? (
          <ContentEditable
            innerRef={contentEditableRef}
            html={title}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => handleChange({ target: { value: e.target.value } })}
            onKeyDown={handleKeyDown}
            placeholder=""
            style={styles.label}
            className="nodrag rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            className="rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
            style={{
              ...styles.label,
              cursor: data.processlink ? 'pointer' : 'default',
            }}
          >
            {hyphenateText(stripHtml(data?.details?.title || data?.label || title || ''), langCode)}
          </div>
        )}
      </div>

      <div style={styles.borderOverlay}></div>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-target"
        style={editable && isHovered ? styles.hoverhandle : styles.handle}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-source"
        style={editable && isHovered ? styles.hoverhandle : styles.handle}
      />
    </div>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '80%',
    height: '72%',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  arrowBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
    color: '#002060',
    width: '100%',
    height: '100%',
    backgroundColor: 'red',
    clipPath:
      'polygon(10px 50%, 0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
    padding: '10px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  label: {
    fontSize: '12px',
    lineHeight: '1.1',
    fontFamily: "'Poppins', sans-serif",
    color: 'white',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    width: '100%',
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    cursor: 'text',
  },
  borderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    clipPath:
      'polygon(10px 50%, 0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
    pointerEvents: 'none',
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    padding: '2px',
  },
  hoverhandle: {
    backgroundColor: '#FF364A',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  handle: {
    backgroundColor: 'transparent',
    border: 'none',
    width: '0px',
    height: '0px',
    pointerEvents: 'none',
  },
};

export default memo(SwimlaneArrowBoxNode);
